import {AxiosRequestConfig} from 'axios';
import {useEffect, useState} from 'react';
import {t} from 'i18n';

import {DevicesSortField, FilterType, LoadState, TestsStatusFilter} from 'enums';
import {IDevice, IDeviceAPI, IDeviceModel, IFilter, IFirmware, IManufacturer, IPagedResponse} from 'interfaces';
// Not using index import for services to avoid circular dependency
import {axiosServer, parseAxiosError} from './API';
import {DeviceInfoService} from './DeviceInfoService';
import {PubSub} from './PubSub';

class _DevicesService {
    public pubSubDevices: PubSub<void>;
    public pubSubDevicesState: PubSub<void>;
    public pubSubFilters: PubSub<void>;
    public pubSubSorting: PubSub<void>;

    public canLoadMore = true;
    // Used for react's list rendering so that it has a key prop.
    private curFilterId = 1;
    // Every time we load, there's a chance the user will do something that will cause another load
    // before the current one finishes. In that case, we want to ignore the results of the first one.
    // We accomplish this by storing the id of each load when it starts. If the curLoadId has changed
    // when it finishes, we know to ignore it.
    private curLoadId = 1;
    public devices: IDevice[] = [];
    public devicesState = LoadState.Initial;
    public filters: IFilter[] = [];
    public loadErrorMessage: string;
    private loadMoreOffset = 0;
    public sortAscending = false;
    private defaultSortAscending = false;
    public sortField = DevicesSortField.CreatedAt;

    private testsStatusLabels = {
        [TestsStatusFilter.All]: '',
        [TestsStatusFilter.AllPass]: t('devicesList.filters.testsStatusAllPass'),
        [TestsStatusFilter.HasFailing]: t('devicesList.filters.testsStatusHasFailing'),
        [TestsStatusFilter.HasUnrun]: t('devicesList.filters.testsStatusHasUnrun'),
    };

    constructor() {
        this.pubSubDevices = new PubSub<void>();
        this.pubSubDevicesState = new PubSub<void>();
        this.pubSubFilters = new PubSub<void>();
        this.pubSubSorting = new PubSub<void>();
    }

    // ----------------------- API Calls -----------------------

    public createDevice(d: IDevice): Promise<IDevice> {
        return axiosServer.post<IDeviceAPI>('/devices', this.mapDeviceToAPIDevice(d))
            .then((response) => {
                let device = this.mapAPIDeviceToDevice(response.data);
                this.devices.push(device);
                return device;
            });
    }

    public updateDevice(d: IDevice): Promise<IDevice> {
        return axiosServer.put<IDeviceAPI>(`/devices/${d.id}`, this.mapDeviceToAPIDevice(d))
            .then((response) => {
                let device = this.mapAPIDeviceToDevice(response.data);
                const i = this.devices.findIndex(dev => dev.id === d.id);
                this.devices.splice(i, 1, device);
                return device;
            });
    }

    public deleteDevice(id: number): Promise<any> {
        return axiosServer.delete<any>(`/devices/${id}`)
            .then((response) => {
                this.devices = this.devices.filter(d => d.id != id);
            });
    }

    public getDevice(id: number): Promise<IDevice> {
        // Need to make sure info is loaded so mapping works.
        const infoPromise = DeviceInfoService.loadIfNeeded();
        const devicePromise = axiosServer.get<IDeviceAPI>(`/devices/${id}`);
        devicePromise.catch((err) => {
            console.error('Error loading device: ', err);
        })
        return Promise.all([infoPromise, devicePromise])
            .then(([deviceInfo, response]) => {
                return this.mapAPIDeviceToDevice(response.data);
            })
    }

    public getDevices(): void {
        const loadId = ++this.curLoadId;
        const oldState = this.devicesState;
        this.devicesState = LoadState.Loading;
        this.devices = [];
        this.loadErrorMessage = undefined;
        this.loadMoreOffset = 0;
        this.canLoadMore = true;

        // We need the manufacturer info so we can fill it in for each device in the mapping function.
        const infoPromise = DeviceInfoService.loadIfNeeded();
        const axiosParams = this.buildDevicesAxiosConfig()
        const devicesPromise = axiosServer.get<IPagedResponse<IDeviceAPI>>('/devices', axiosParams);

        Promise.all([infoPromise, devicesPromise])
            .then(([deviceInfo, response]) => {
                // Do nothing if another load has started after this one.
                if (this.curLoadId !== loadId) return;

                // Make sure the data looks like we expect
                if (response.data && response.data.results) {
                    this.devicesState = LoadState.Success;
                    this.loadMoreOffset = response.data.paging.count_in_page;
                    // If we got less results than the limit, we've loaded all we can.
                    this.canLoadMore = response.data.paging.count_in_page === response.data.paging.limit;
                    this.devices = response.data.results.map(this.mapAPIDeviceToDevice);
                } else {
                    this.devicesState = LoadState.Error;
                    this.loadErrorMessage = 'ERROR loading devices: ' + response.data;
                }

                this.pubSubDevicesState.publish();
                this.pubSubDevices.publish();
            })
            .catch((err) => {
                if (this.curLoadId !== loadId) return;
                this.onAPIError(err);
            })

        this.pubSubDevicesState.publish();
        this.pubSubDevices.publish();
    }

    public loadMoreDevices() {
        if (this.devicesState === LoadState.Loading || this.devicesState === LoadState.Initial) {
            // If a loadmore is already in progress, do nothing. We should never be in initial state here.
            return;
        }
        if (!this.canLoadMore) return;

        const loadId = ++this.curLoadId;
        this.devicesState = LoadState.Loading;
        this.loadErrorMessage = undefined;

        const axiosParams = this.buildDevicesAxiosConfig(this.loadMoreOffset);
        axiosServer.get<IPagedResponse<IDeviceAPI>>('/devices', axiosParams)
            .then((response) => {
                // Do nothing if another load has started after this one.
                if (this.curLoadId !== loadId) return;

                this.devicesState = LoadState.Success;
                this.loadMoreOffset = this.loadMoreOffset + response.data.paging.count_in_page;
                this.canLoadMore = response.data.paging.count_in_page === response.data.paging.limit;

                // Append new data. BUT, need to dedupe it because it's possible that a new device could
                // have been added, which would shift our result set because we're just doing naive limit/offset paging.
                // There are more advanced paging techniques, but this should be good enough because devices
                // rarely get added/removed.
                const existingMap: {[deviceId: string]: true} = {};
                for (const d of this.devices) {
                    existingMap[d.id] = true;
                }
                const newResults = response.data.results
                    .map(this.mapAPIDeviceToDevice)
                    // Filter out duplicates
                    .filter(device => !existingMap[device.id]);

                this.devices = this.devices.concat(newResults);

                this.pubSubDevicesState.publish();
                this.pubSubDevices.publish();
            })
            .catch((err) => {
                if (this.curLoadId !== loadId) return;
                this.onAPIError(err);
            });

        this.pubSubDevicesState.publish();
    }

    // Apply our filters to build the query params object for axios.
    // We're using URLSearchParams directly because we need multiple params with the same name for filters.
    // https://stackoverflow.com/questions/42898009/multiple-fields-with-same-key-in-query-params-axios-request
    private buildDevicesAxiosConfig(offset = 0): AxiosRequestConfig {
        const searchParams = new URLSearchParams();
        searchParams.append('offset', offset + '');
        searchParams.append('sort_by', this.sortField);
        searchParams.append('sort_direction', this.sortAscending ? 'asc' : 'desc');

        const filtersMap = {
            [FilterType.Firmware]: 'firmware_version_id',
            [FilterType.Location]: 'location',
            [FilterType.Manufacturer]: 'manufacturer_id',
            [FilterType.Model]: 'model_id',
            [FilterType.TestsStatus]: 'test_statuses',
        };
        for (const f of this.filters) {
            const paramName = filtersMap[f.type];
            if (paramName) {
                searchParams.append(paramName, f.value + '');
            } else {
                console.log('ERROR: unsupported filter for query param: ', f);
            }
        }

        return {
            params: searchParams,
        };
    }

    private onAPIError = (err: any) => {
        this.devicesState = LoadState.Error;
        this.loadErrorMessage = 'ERROR loading devices: ' + parseAxiosError(err);
        this.pubSubDevicesState.publish();
        this.pubSubDevices.publish();
    }

    // ----------------------- Filtering/Sorting -----------------------

    public addManufacturerFilter(manufacturerId: number): void {
        const man = DeviceInfoService.manufacturersById[manufacturerId];
        if (man) {
            this.addFilter({
                id: ++this.curFilterId,
                type: FilterType.Manufacturer,
                label: man.name,
                value: manufacturerId,
            });
        }
    }

    public addModelFilter(modelId: number): void {
        const model = DeviceInfoService.modelsById[modelId];
        if (model) {
            this.addFilter({
                id: ++this.curFilterId,
                type: FilterType.Model,
                label: model.name,
                value: modelId,
            });
        }
    }

    public addFirmwareFilter(firmwareId: number): void {
        const firmware = DeviceInfoService.firmwaresById[firmwareId];
        if (firmware) {
            this.addFilter({
                id: ++this.curFilterId,
                type: FilterType.Firmware,
                label: firmware.firmware_version,
                value: firmwareId,
            });
        }
    }

    public addLocationFilter(location: string): void {
        this.addFilter({
            id: ++this.curFilterId,
            type: FilterType.Location,
            label: location,
            value: location,
        });
    }

    public setTestsStatusFilter(testsStatus: TestsStatusFilter): void {
        // Remove any existing tests status filter
        this.filters = this.filters.filter(f => f.type !== FilterType.TestsStatus);
        if (testsStatus !== TestsStatusFilter.All) {
            this.addFilter({
                id: ++this.curFilterId,
                type: FilterType.TestsStatus,
                label: this.testsStatusLabels[testsStatus],
                value: testsStatus,
            })
        } else {
            this.pubSubFilters.publish();
        }
    }

    private addFilter(newFilter: IFilter): void {
        // Prevent adding duplicates
        const existing = this.filters.find((f) => {
            return f.type === newFilter.type && f.value === newFilter.value;
        })
        if (!existing) {
            this.filters = [...this.filters, newFilter];
            this.sortFilters();
            this.pubSubFilters.publish();
        }
    }

    private sortFilters(): void {
        const typeOrder = {
            [FilterType.Manufacturer]: 1,
            [FilterType.Model]: 2,
            [FilterType.Firmware]: 3,
            [FilterType.Location]: 4,
            [FilterType.TestsStatus]: 5,
        }

        this.filters.sort((a, b) => {
            if (a.type !== b.type) {
                return typeOrder[a.type] - typeOrder[b.type];
            }
            // If 2 filters are the same type, do case-insensitive natural sort.
            // Natural sort means "10" goes after "9".
            return a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'});
        })
    }

    public removeFilter(filter: IFilter): void {
        this.filters = this.filters.filter(f => f.id !== filter.id);
        this.pubSubFilters.publish();
    }

    public toggleSortField(field: DevicesSortField, devices: IDevice[]): void {
        if (field === this.sortField) {
            // Field is already selected, toggle the direction.
            this.sortAscending = !this.sortAscending;
        } else {
            // Field is not selected, select it and switch to default sort order
            this.sortField = field;
            this.sortAscending = this.defaultSortAscending;
        }
        this.pubSubSorting.publish();
        this.getDevices();
    }

    // ----------------------- Hooks -----------------------

    public useDevices = (): IDevice[] => {
        const [devices, setDevices] = useState(this.devices);
        useEffect(() => {
            return this.pubSubDevices.subscribe(() => {
                setDevices(this.devices);
            });
        });
        return devices;
    }

    public useDevicesState = (): LoadState => {
        const [devicesState, setDevicesState] = useState(this.devicesState);
        useEffect(() => {
            return this.pubSubDevicesState.subscribe(() => {
                setDevicesState(this.devicesState);
            });
        })
        return devicesState;
    }

    public useFilters = (): IFilter[] => {
        const [filters, setFilters] = useState(this.filters);
        useEffect(() => {
            return this.pubSubFilters.subscribe(() => {
                setFilters(this.filters);
            });
        });
        return filters;
    }

    public useSorting = (): {sortAscending: boolean, sortField: DevicesSortField} => {
        const [sortAscending, setAscending] = useState(this.sortAscending);
        const [sortField, setField] = useState(this.sortField);
        useEffect(() => {
            return this.pubSubSorting.subscribe(() => {
                setAscending(this.sortAscending);
                setField(this.sortField);
            });
        })
        return {sortAscending, sortField};
    }

    // ----------------------- API Mapping -----------------------

    private mapAPIDeviceToDevice = (d: IDeviceAPI): IDevice => {
        let model = DeviceInfoService.modelsById[d.model_id];
        let man: IManufacturer;
        if (model) {
            man = DeviceInfoService.manufacturersById[model.manufacturer_id];
            if (!man) {
                console.error('Manufacturer not found for device: ', d);
            }
        } else {
            console.error('Model not found for device: ', d);
            // Even if we can't find the model, add an empty model so the id is preserved.
            model = <IDeviceModel><any>{
                id: d.model_id,
            };
        }

        let firmware = DeviceInfoService.firmwaresById[d.firmware_version_id];
        if (!firmware) {
            console.error('Firmware not found for device: ', d);
            firmware = <IFirmware><any>{
                id: d.firmware_version_id,
            };
        }

        return {
            id: d.id,
            manufacturer: man,
            model: model,
            firmware: firmware,
            location: d.location,
            serial_number: d.serial_number,
            mac_address: d.mac_address,
            created_at: new Date(d.created_at),

            last_run_id: d.last_run_id,
            last_run_at: d.last_run_at ? new Date(d.last_run_at) : undefined,
            tests_passed: d.tests_passed || 0,
            tests_total: d.tests_total || 0,
        }
    }

    private mapDeviceToAPIDevice = (d: IDevice): IDeviceAPI => {
        return {
            id: d.id,
            model_id: d.model.id,
            firmware_version_id: d.firmware.id,
            location: d.location,
            serial_number: d.serial_number,
            mac_address: d.mac_address,
        }
    }
}

export const DevicesService = new _DevicesService();